
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance, { publicRequest } from './../../config/axios';

export const getCoins = createAsyncThunk('rango/getCoins', async () => {
    const url = `/wallet/public/coins`;
    const response = await axiosInstance({
        method: 'GET',
        url,
    });
    return response.data;
});

export const getCoin = createAsyncThunk('rango/getCoin', async (coinId) => {
    const url = `/rango/coins/${coinId}`;
    const response = await publicRequest({
        method: 'GET',
        url,
    });
    return response.data;
});

export const getCoinHistory = createAsyncThunk('rango/getCoinHistory', async (coinId) => {
    const url = `/rango/coins/${coinId}/history`;
    const response = await publicRequest({
        method: 'GET',
        url,
    });
    return response.data;
});

export const getRoutes = createAsyncThunk('rango/getRoutes', async ({
    from,
    to,
    amount,
}, { rejectWithValue }) => {
    try {
        const url = '/rango/routes';
        const response = await axiosInstance({
            method: 'POST',
            url,
            data: {
                from,
                to,
                amount,
            },
        });
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch routes';
        return rejectWithValue(message);
    }
});

export const clearRoutes = createAsyncThunk('rango/clearRoutes', async () => {
    return [];
});

export const swap = createAsyncThunk('rango/swap', async (swapData, { rejectWithValue }) => {
    try {
        const url = '/rango/swap';
        const response = await axiosInstance({
            method: 'POST',
            url,
            data: swapData,
        });
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Swap failed';
        return rejectWithValue(message);
    }
});

export const getHistory = createAsyncThunk('rango/getHistory', async (mode = 'public') => {
    const url = `/rango/history?mode=${mode}`;
    const response = await axiosInstance({
        method: 'GET',
        url,
    });
    return response.data;
});

const initialState = {
    coins: [],
    loading: true,
    coin: null,
    history: [],
    routes: [],
    error: null,
};

const rangoSlice = createSlice({
    name: 'rango',
    initialState,
    reducers: {
        setloading: (state, action) => {
            state.loading = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCoins.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCoins.fulfilled, (state, action) => {
                console.log('Coins data:', action.payload);
                state.coins = action.payload.data;
                state.loading = false;
            })
            .addCase(getCoins.rejected, (state, action) => {
                state.loading = false;
                console.error('Failed to fetch coins:', action.error);
            })
            .addCase(getCoin.fulfilled, (state, action) => {
                state.coin = action.payload;
                state.loading = false;
            })
            .addCase(getCoinHistory.fulfilled, (state, action) => {
                state.coinHistory = action.payload;
                state.loading = false;
            })
            .addCase(getRoutes.pending, (state) => {
                state.routes = [];
                state.loading = true;
                state.error = null;
            })
            .addCase(getRoutes.fulfilled, (state, action) => {
                console.log('Routes data:', action.payload.results);
                state.routes = action.payload.results;
                state.loading = false;
                if (action.payload.results.length === 0) {
                    state.error = 'No routes available for the selected tokens and amount.';
                }
            })
            .addCase(getRoutes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
                console.error('Failed to fetch routes:', action.payload || action.error.message);
            })
            .addCase(swap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(swap.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(swap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
                console.error('Swap failed:', action.payload || action.error.message);
            })
            .addCase(getHistory.pending, (state) => {
                state.loading = true;
            })
            .addCase(getHistory.fulfilled, (state, action) => {
                console.log('History data:', action.payload);
                state.history = action.payload;
                state.loading = false;
            })
            .addCase(getHistory.rejected, (state, action) => {
                state.loading = false;
                console.error('Failed to fetch history:', action.error);
            })
            .addCase(clearRoutes.fulfilled, (state) => {
                state.routes = [];
            });
    }
});

export const { setloading } = rangoSlice.actions;

// Export selectors
export const selectCoins = (state) => state.rango.coins;
export const selectCoin = (state) => state.rango.coin;
export const selectCoinHistory = (state) => state.rango.coinHistory;
export const selectLoading = (state) => state.rango.loading;

export default rangoSlice.reducer;

